import {
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
} from '@ads/components-react';
import {
  EllipsisVerticalIcon,
  LanguageIcon,
  PencilSquareIcon,
  TrashIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SerieGridItemViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface SerieTableProps {
  readonly data: readonly SerieGridItemViewModel[];
  readonly isLoading: boolean;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (serie: SerieGridItemViewModel) => void;
  readonly onManageVideos: (serie: SerieGridItemViewModel) => void;
  readonly onManageLangs: (serie: SerieGridItemViewModel) => void;
  readonly onDelete: (serie: SerieGridItemViewModel) => void;
}

/**
 * Tabela de Séries. Colunas Título e Ativo (Tag), com um menu de ações "..."
 * agrupando Editar/Vídeos/Idiomas e, após um divisor, Excluir — mesmo padrão
 * de menu com várias opções. Delega loading/empty/paginação (server-side) ao
 * `DataTable`. Fiel ao frame *Série · Listagem* de `design/series.pen`.
 */
export function SerieTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onManageVideos,
  onManageLangs,
  onDelete,
}: SerieTableProps) {
  const { t } = useTranslation('series');

  const columns = useMemo<ColumnDef<SerieGridItemViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'langName',
        header: t('series.table.columns.title'),
        cell: info => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: t('series.table.columns.active'),
        cell: ({ row }) => {
          const active = row.original.active ?? false;
          return (
            <Tag
              label={t(active ? 'series.table.statusActive' : 'series.table.statusInactive')}
              size="md"
              status={active ? 'positive' : 'default'}
              custom={{ text: 'text-nowrap' }}
            />
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('series.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end">
            <Dropdown
              trigger={
                <IconButton
                  icon={EllipsisVerticalIcon}
                  ariaLabel={t('series.table.columns.actions')}
                  hierarchy="tertiary"
                  size="md"
                />
              }
            >
              <DropdownItem onSelect={() => onEdit(row.original)}>
                <DropdownItemContent leadingIcon={PencilSquareIcon}>
                  {t('series.actions.edit')}
                </DropdownItemContent>
              </DropdownItem>
              <DropdownItem onSelect={() => onManageVideos(row.original)}>
                <DropdownItemContent leadingIcon={VideoCameraIcon}>
                  {t('series.actions.videos')}
                </DropdownItemContent>
              </DropdownItem>
              <DropdownItem onSelect={() => onManageLangs(row.original)}>
                <DropdownItemContent leadingIcon={LanguageIcon}>
                  {t('series.actions.langs')}
                </DropdownItemContent>
              </DropdownItem>
              <DropdownItem status="destructive" onSelect={() => onDelete(row.original)}>
                <DropdownItemContent leadingIcon={TrashIcon}>
                  {t('series.actions.delete')}
                </DropdownItemContent>
              </DropdownItem>
            </Dropdown>
          </div>
        ),
      },
    ],
    [t, onEdit, onManageVideos, onManageLangs, onDelete],
  );

  return (
    <DataTable
      data={data as SerieGridItemViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('series.table.empty')}
      emptyStateDescription={t('series.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
    />
  );
}
